import { Navigate, Outlet, Route, Routes } from "react-router-dom";

import { ModalProvider } from "@contexts";
import { Routes as AppRoutes } from "@ctypes";
import { AuthFlow, OnboardingFlow } from "@entry";
import { useRefreshToken } from "@hooks";
import { AccountsRoute } from "@routes/AccountsRoute";
import { AllocateFundsRoute } from "@routes/AllocateFundsRoute";
import { AlpacaAccountDocumentsRoute } from "@routes/AlpacaAccountDocumentsRoute";
import { ContributionBankAccountRoute } from "@routes/ContributionBankAccountRoute";
import { ContributionGoalRoute } from "@routes/ContributionGoalRoute";
import { ContributionIndividualRoute } from "@routes/ContributionIndividualRoute";
import { DashboardRoute } from "@routes/DashboardRoute";
import { DocumentCategoryRoute } from "@routes/DocumentCategoryRoute";
import { DocumentsRoute } from "@routes/DocumentsRoute";
import { DownloadAppRoute } from "@routes/DownloadAppRoute";
import { EditGoalRoute } from "@routes/EditGoalRoute";
import { EmploymentInformationRoute } from "@routes/EmploymentInformationRoute";
import { GoalBreakdownRoute } from "@routes/GoalBreakdownRoute";
import { GoalsOverviewRoute } from "@routes/GoalsOverviewRoute";
import { LinkedAccountsRoute } from "@routes/LinkedAccountsRoute";
import { NotFoundRoute } from "@routes/NotFoundRoute";
import { OAuthCallbackRoute } from "@routes/OAuthCallbackRoute";
import { OneTimeContributionRoute } from "@routes/OneTimeContributionRoute";
import { PersonalInformationRoute } from "@routes/PersonalInformationRoute";
import { PhoneAuthCallbackRoute } from "@routes/PhoneAuthCallbackRoute";
import { ProfileRoute } from "@routes/ProfileRoute";
import { RedeemCodeRoute } from "@routes/RedeemCodeRoute";
import { PartnershipRoute } from "@routes/PartnershipRoute";
import { ReferralDashboardRoute } from "@routes/ReferralDashboardRoute";
import { RequestGiftRoute } from "@routes/RequestGiftRoute";
import { ResetPasswordRoute } from "@routes/ResetPasswordRoute";
import { ResetPasswordSuccessRoute } from "@routes/ResetPasswordSuccessRoute";
import { ShareGoalContactRoute } from "@routes/ShareGoalContactRoute";
import { ShareGoalRoute } from "@routes/ShareGoalRoute";
import { SubscriptionRoute } from "@routes/SubscriptionRoute";
import { TransfersRoute } from "@routes/TransfersRoute";
import { TrustedContactRoute } from "@routes/TrustedContactRoute";
import { WithdrawRoute } from "@routes/WithdrawRoute";
import { getNextRoute } from "@utils";

export const Router = () => {
  useRefreshToken();

  return (
    <Routes>
      {/* Auth provider callbacks — need real URLs */}
      <Route path={AppRoutes.PhoneLogin} element={<PhoneAuthCallbackRoute />} />
      <Route path={AppRoutes.OAuthCallback} element={<OAuthCallbackRoute />} />
      <Route path={AppRoutes.ResetPassword} element={<ResetPasswordRoute />} />
      <Route path={AppRoutes.ResetPasswordSuccess} element={<ResetPasswordSuccessRoute />} />

      {/* Auth — pre-login flow */}
      <Route path={AppRoutes.Auth} element={<AuthFlow />} />

      {/* Onboarding — post-login KYC stepper */}
      <Route path={AppRoutes.Onboarding} element={<OnboardingFlow />} />

      {/* Authenticated app */}
      <Route element={<AppLayout />}>
        <Route index element={<IndexRedirect />} />
        <Route path={AppRoutes.Dashboard} element={<DashboardRoute />} />
        <Route path={AppRoutes.Partnership} element={<PartnershipRoute />} />
        <Route path={AppRoutes.Accounts} element={<AccountsRoute />} />
        <Route path={AppRoutes.Transfers} element={<TransfersRoute />} />
        <Route path={AppRoutes.Profile} element={<ProfileRoute />} />
        <Route path={AppRoutes.PersonalInformation} element={<PersonalInformationRoute />} />
        <Route path={AppRoutes.EmploymentInformation} element={<EmploymentInformationRoute />} />
        <Route path={AppRoutes.LinkedAccounts} element={<LinkedAccountsRoute />} />
        <Route path={AppRoutes.OneTimeContribution} element={<OneTimeContributionRoute />} />
        <Route path={AppRoutes.ContributionIndividual} element={<ContributionIndividualRoute />} />
        <Route path={AppRoutes.ContributionGoal} element={<ContributionGoalRoute />} />
        <Route path={AppRoutes.ContributionBankAccount} element={<ContributionBankAccountRoute />} />
        <Route path={AppRoutes.GoalsOverview} element={<GoalsOverviewRoute />} />
        <Route path={AppRoutes.GoalBreakdown} element={<GoalBreakdownRoute />} />
        <Route path={AppRoutes.EditGoal} element={<EditGoalRoute />} />
        <Route path={AppRoutes.ShareGoal} element={<ShareGoalRoute />} />
        <Route path={AppRoutes.ShareGoalContact} element={<ShareGoalContactRoute />} />
        <Route path={AppRoutes.Withdraw} element={<WithdrawRoute />} />
        <Route path={AppRoutes.RedeemCode} element={<RedeemCodeRoute />} />
        <Route path={AppRoutes.ReferralDashboard} element={<ReferralDashboardRoute />} />
        <Route path={AppRoutes.RequestGift} element={<RequestGiftRoute />} />
        <Route path={AppRoutes.Subscription} element={<SubscriptionRoute />} />
        <Route path={AppRoutes.AllocateFunds} element={<AllocateFundsRoute />} />
        <Route path={AppRoutes.TrustedContact} element={<TrustedContactRoute />} />
        <Route path={AppRoutes.Documents} element={<DocumentsRoute />} />
        <Route path={AppRoutes.DocumentCategory} element={<DocumentCategoryRoute />} />
        <Route path={AppRoutes.AlpacaAccountDocuments} element={<AlpacaAccountDocumentsRoute />} />
        <Route path={AppRoutes.DownloadApp} element={<DownloadAppRoute />} />
      </Route>

      <Route path="*" element={<NotFoundRoute />} />
    </Routes>
  );
};

const IndexRedirect = () => {
  const next = getNextRoute();
  return <Navigate to={next} replace />;
};

const AppLayout = () => (
  <ModalProvider>
    <Outlet />
  </ModalProvider>
);
